/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { Siren, Phone, Navigation, Loader2, MapPinOff, RefreshCw, Building2, WifiOff } from "lucide-react";

const SIGN_LBL = { visage: "Visage", bras: "Bras", parole: "Parole", urgence: "Urgence" };
const KINSHASA = [-4.325, 15.3222];

const pin = (color, traite) => L.divIcon({
  className: "",
  html: `<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};opacity:${traite ? 0.55 : 1};border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.35);display:grid;place-items:center"><div style="transform:rotate(45deg);width:10px;height:10px;border-radius:50%;background:${traite ? "#5CB85C" : "#fff"}"></div></div>`,
  iconSize: [34, 34], iconAnchor: [17, 34], popupAnchor: [0, -34],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 14); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

/** Carte des signalements. `clinic` = tous les praticiens de la clinique, une couleur par praticien + légende filtrante. */
export function WorryMap({ compact = false, clinic = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [jours, setJours] = useState(1);
  const [hidden, setHidden] = useState([]);
  const [stale, setStale] = useState(null);
  const endpoint = clinic ? "/clinic/worries/map" : "/therapist/worries/map";
  const cacheKey = `bc_map_${clinic ? "clinic" : "th"}_${jours}`;
  const load = () => api.get(`${endpoint}?jours=${jours}`)
    .then(({ data }) => { setData(data); setErr(null); setStale(null); localStorage.setItem(cacheKey, JSON.stringify({ data, at: new Date().toISOString() })); })
    .catch((e) => {
      const cached = (() => { try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; } })();
      if (cached && (!e.response || e.response.status >= 500)) { setData(cached.data); setStale(cached.at); setErr(null); }
      else setErr(e.response?.status || 500);
    });
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [jours]); // eslint-disable-line
  useEffect(() => { window.addEventListener("bc-worries-changed", load); return () => window.removeEventListener("bc-worries-changed", load); }, [jours]); // eslint-disable-line

  if (err) return <div data-testid="worry-map-error" className="text-[13px] text-[#D9534F]">{err === 402 ? "Carte réservée au forfait Clinique." : err === 404 ? "Créez d'abord votre clinique." : "Carte indisponible."}</div>;
  if (!data) return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  const visible = data.avec_position.filter((w) => !clinic || !hidden.includes(w.therapeute_id));
  const pts = visible.map((w) => [w.position.lat, w.position.lng]);
  const colorOf = (w) => (clinic ? w.couleur || "#0A1426" : "#D9534F");
  const toggleTh = (id) => setHidden((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]));

  return (
    <div data-testid={clinic ? "clinic-map-card" : "worry-map-card"} className="rounded-3xl bg-white border border-[#E4E0D5] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 py-3 flex-wrap">
        <div className="flex items-center gap-2">
          {clinic ? <Building2 size={18} className="text-[#0A1426]" /> : <Siren size={18} className="text-[#D9534F]" />}
          <div>
            <div className="font-[Fraunces] text-base font-bold text-[#0A1426] leading-tight">{clinic ? `Carte Clinique — ${data.clinic?.nom || ""}` : "Carte live des signalements"}</div>
            <div data-testid={clinic ? "clinic-map-summary" : "worry-map-summary"} className="text-[11px] text-[#0A1426]/60">{jours === 1 ? "Aujourd'hui" : `${jours} derniers jours`} · {visible.length} localisé(s) · {data.ouverts} à traiter / {data.total}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[[1, "Jour"], [3, "3 j"], [7, "7 j"]].map(([k, l]) => (
            <button key={k} data-testid={`${clinic ? "clinic" : "worry"}-map-range-${k}`} onClick={() => setJours(k)} className={`h-8 px-3 rounded-full text-[11px] font-bold ${jours === k ? "bg-[#0A1426] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}>{l}</button>
          ))}
          <button data-testid="worry-map-refresh" onClick={load} aria-label="Actualiser" className="w-8 h-8 rounded-full bg-[#F5F3EE] text-[#0A1426] grid place-items-center"><RefreshCw size={13} /></button>
        </div>
      </div>
      {clinic && (
        <div data-testid="clinic-map-legend" className="px-4 pb-2 flex flex-wrap gap-1.5">
          {(data.praticiens || []).map((t) => (
            <button key={t.id} data-testid={`clinic-map-legend-${t.id}`} onClick={() => toggleTh(t.id)} aria-pressed={!hidden.includes(t.id)}
              className={`h-8 pl-2 pr-3 rounded-full border text-[11px] font-bold inline-flex items-center gap-1.5 transition-opacity ${hidden.includes(t.id) ? "opacity-40 border-[#E4E0D5] bg-white text-[#0A1426]" : "border-transparent text-white"}`}
              style={hidden.includes(t.id) ? undefined : { backgroundColor: t.couleur }}>
              <span className="w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: t.couleur }} /> {t.nom} · {t.signalements}
            </button>
          ))}
        </div>
      )}
      {stale && <div data-testid="worry-map-offline" className="mx-4 mb-2 rounded-xl bg-[#D4AF5A] text-[#0A1426] text-[11px] font-bold px-3 py-1.5 flex items-center gap-2"><WifiOff size={12} /> Hors-ligne — données du {new Date(stale).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · tuiles déjà vues affichées</div>}
      <div data-testid="worry-map" className={`relative z-0 ${compact ? "h-[260px]" : "h-[380px]"}`}>
        <MapContainer center={data.centre ? [data.centre.lat, data.centre.lng] : KINSHASA} zoom={12} scrollWheelZoom={!compact} className="h-full w-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={pts} />
          {visible.map((w) => (
            <Marker key={w.id} position={[w.position.lat, w.position.lng]} icon={pin(colorOf(w), w.statut === "traite")}>
              <Popup>
                <div className="flex flex-col gap-1 min-w-[190px]" data-testid="worry-map-popup">
                  <div className="font-bold text-[#0A1426]">{w.patient_nom}</div>
                  {clinic && <div className="text-[11px] font-bold" style={{ color: w.couleur }}>{w.therapeute_nom}</div>}
                  <div className="text-[11px] text-[#0A1426]/70">{new Date(w.date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {w.statut === "traite" ? "Traité" : "À traiter"}</div>
                  <div className="text-[11px]">Signes : {(w.signes || []).map((s) => SIGN_LBL[s] || s).join(", ") || "aucun"}</div>
                  <div className="text-[11px]">Par {w.aidant?.nom || "—"}{w.position.precision_m ? ` · ± ${w.position.precision_m} m` : ""}</div>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    <a data-testid="worry-map-route" href={`https://www.google.com/maps/dir/?api=1&destination=${w.position.lat},${w.position.lng}`} target="_blank" rel="noreferrer" style={{ color: "#fff" }} className="h-8 px-3 rounded-full bg-[#2BA3A0] text-white text-[11px] font-bold inline-flex items-center gap-1"><Navigation size={12} /> Itinéraire</a>
                    {w.aidant?.telephone && <a data-testid="worry-map-call" href={`tel:${w.aidant.telephone}`} style={{ color: "#fff" }} className="h-8 px-3 rounded-full bg-[#0A1426] text-white text-[11px] font-bold inline-flex items-center gap-1"><Phone size={12} /> Rappeler</a>}
                    <button data-testid="worry-map-patient" onClick={() => navigate(`/pro/patients/${w.patient_id}`)} className="h-8 px-3 rounded-full bg-[#F5F3EE] text-[#0A1426] text-[11px] font-bold">Fiche</button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {data.sans_position.length > 0 && (
        <div data-testid="worry-map-nopos" className="px-4 py-2.5 border-t border-[#F5F3EE] text-[11px] text-[#0A1426]/70 flex items-start gap-2">
          <MapPinOff size={13} className="mt-0.5 shrink-0" />
          <span>Sans position : {data.sans_position.slice(0, 5).map((w) => `${w.patient_nom}${clinic ? ` (${w.therapeute_nom})` : ""} (${new Date(w.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })})`).join(", ")}{data.sans_position.length > 5 ? ` +${data.sans_position.length - 5}` : ""}</span>
        </div>
      )}
      {data.total === 0 && <div data-testid="worry-map-empty" className="px-4 py-2.5 border-t border-[#F5F3EE] text-[12px] text-[#0A1426]/60">Aucun signalement sur la période.</div>}
    </div>
  );
}
